import React, { useEffect, useMemo, useState } from "react";
import { Col, Container, Form, Row, Table } from "react-bootstrap";
import { CSSTransition, TransitionGroup } from "react-transition-group";
import { MinusIcon, PlusIcon, TimesIcon, ViewIcon } from "@sx-icons";
import { CreateGUID, GetSelfWeightGuid } from "@common/helpers/funlib";
import { useAppSelector } from "../app/hooks";
//import { AppContext, IAppContext } from "../store/AppContext";
import {
  SXEditField,
  removeInvalidTextFromNumberString,
  UnitCreator,
} from "@common/components/ui-components/SXEditField";
import { SXDropdown } from "@common/components/ui-components/SXDropdown";
import { t } from "i18next";
import { ng } from "@common/ngds";
import { useTranslation } from "react-i18next";
import { useProject } from "../features/projects/projectSlice";
import { updateProject } from "../features/ngds/ngdsSlice";
import { LoadCaseColor } from "./SXProjectInput";
import { getColumnLength } from "@common/helpers/defaultObject";
import { useUnit } from "../features/unit/unitSlice";
import { report_exception } from "./ui-components/SXAlert/SXAlertMessage";

// --- Main component ---

interface ProjectInputLoadsProps {}

export const SXInputLoads = (props: ProjectInputLoadsProps) => {
  const { t, i18n } = useTranslation();

  const {
    project: selProject,
    addLoad,
    updateLoad,
    updateLoadPosition,
    updateLoadValue,
    deleteLoad,
    addSelfWeights,
    delSelfWeights,
  } = useProject();
  const {
    lengthUnit,
    forceUnit,
    sectionalUnit,
    lineLoadUnit,
    toDisplayLength,
    fromDisplayLength,
    toDisplayForce,
    fromDisplayForce,
    toDisplayLineLoad,
    fromDisplayLineLoad,
    toDisplaySectionalLength,
    fromDisplaySectionalLength,
  } = useUnit();

  const inputJson = selProject?.data.data?.input;
  const structureJson = inputJson?.structures?.[0];
  const elementJson = structureJson?.elements?.[0] || null;
  const loadingJson = structureJson?.loading || null;
  const loading = loadingJson ? ng.loading.Data.fromObject(loadingJson) : null;
  const loadCases = useMemo(() => loading?.loadcases || [], [loadingJson]);
  const loads = useMemo(() => loading?.loads || [], [loadingJson]);
  const loadCaseOptions = useMemo(
    () =>
      loadCases.filter((lc) => !!lc?.id?.guid).map((lc) => ({ id: lc.id!.guid!, name: lc.id?.name || lc.id!.guid! })),
    [loadCases],
  );
  const horizontalDirectionOptions = useMemo(
    () => [
      { id: "Y", name: "Y" },
      { id: "Z", name: "Z" },
    ],
    [],
  );
  const verticalDirectionOptions = useMemo(() => [{ id: "X", name: "X" }], []);

  const { loadsVertical, loadsHorizontal, lineLoads, elementLength } = useMemo(() => {
    const element = elementJson ? ng.element.Data.fromObject(elementJson) : null;
    const input = inputJson ? ng.input.Data.fromObject(inputJson) : null;
    const elementLength = element && input ? getColumnLength(element, input) : 0;

    const vertical: ng.load.Data[] = [];
    const horizontal: ng.load.Data[] = [];
    const lines: ng.load.Data[] = [];

    for (const load of loads) {
      if (load.type !== ng.load.Type.TYPE_FORCE) continue;

      if (load.distribution === ng.load.DistributionType.DISTRIBUTION_TYPE_POINT) {
        const isVertical = load.direction?.x === -1 && load.direction?.y === 0 && load.direction?.z === 0;
        if (isVertical) {
          vertical.push(load);
        } else {
          // Horizontal point loads act in global +Y or +Z with no X component.
          const isHorizontal = load.direction?.x === 0 && (load.direction?.y > 0 || load.direction?.z > 0);
          if (isHorizontal) {
            horizontal.push(load);
          }
        }
      } else if (load.distribution === ng.load.DistributionType.DISTRIBUTION_TYPE_LINE) {
        lines.push(load);
      }
    }

    return { loadsVertical: vertical, loadsHorizontal: horizontal, lineLoads: lines, elementLength: elementLength };
  }, [loads, elementJson, inputJson]);

  const [activeSW, setActiveSW] = useState(false);

  useEffect(() => {
    let guid = GetSelfWeightGuid(loadCases);
    if (guid !== "") {
      setActiveSW(true);
    } else {
      setActiveSW(false);
    }
  }, [, loadCases]);

  const clampPosition = (value: number) => {
    if (!Number.isFinite(value)) return 0;
    const minClamped = Math.max(value, 0);
    if (!Number.isFinite(elementLength) || elementLength <= 0) {
      return minClamped;
    }
    return Math.min(minClamped, elementLength);
  };

  const addNewVerticalLoad = (length: number) => {
    const newLoad = ng.load.Data.create({
      id: ng.utils.ID.create({ guid: CreateGUID(), name: "Vertical Load" }),
      type: ng.load.Type.TYPE_FORCE,
      distribution: ng.load.DistributionType.DISTRIBUTION_TYPE_POINT,
      positions: [ng.geometry.Point3D.create({ x: length, y: 0, z: 0 })],
      direction: ng.geometry.Vector3D.create({ x: -1, y: 0, z: 0 }),
      values: [20.0e3],
      lcaseGuid: loading?.loadcases?.[0]?.id?.guid || "",
      assignedObjects: elementJson?.id?.guid ? [elementJson.id.guid] : [],
    });
    addLoad(newLoad);
  };

  const addNewHorizontalLoad = (length: number) => {
    const newLoad = ng.load.Data.create({
      id: ng.utils.ID.create({ guid: CreateGUID(), name: "Horizontal Load" }),
      type: ng.load.Type.TYPE_FORCE,
      distribution: ng.load.DistributionType.DISTRIBUTION_TYPE_POINT,
      positions: [ng.geometry.Point3D.create({ x: length / 2, y: 0, z: 0 })],
      direction: ng.geometry.Vector3D.create({ x: 0, y: 1, z: 0 }),
      values: [20e3],
      lcaseGuid: loading?.loadcases?.[0]?.id?.guid || "",
      assignedObjects: elementJson?.id?.guid ? [elementJson.id.guid] : [],
    });
    addLoad(newLoad);
  };

  const addNewLineLoad = (length: number) => {
    const newLoad = ng.load.Data.create({
      id: ng.utils.ID.create({ guid: CreateGUID(), name: "Line Load" }),
      type: ng.load.Type.TYPE_FORCE,
      distribution: ng.load.DistributionType.DISTRIBUTION_TYPE_LINE,
      positions: [
        ng.geometry.Point3D.create({ x: 0, y: 0, z: 0 }),
        ng.geometry.Point3D.create({ x: length, y: 0, z: 0 }),
      ],
      direction: ng.geometry.Vector3D.create({ x: 0, y: 1, z: 0 }),
      values: [5.0e3, 5.0e3], // q0, q1
      lcaseGuid: loading?.loadcases?.[0]?.id?.guid || "",
      assignedObjects: elementJson?.id?.guid ? [elementJson.id.guid] : [],
    });
    addLoad(newLoad);
  };

  return (
    <Container fluid id="sx-table-container" className="ps-0 px-0">
      <Container style={{ color: "#696969" }}>
        <Row className="container-row" style={{ color: "#696969", alignItems: "center", marginBottom: 10 }}>
          <Form.Check
            type="switch"
            className="sx-switch"
            id="selfWeightswitch"
            label={t("self-weight_.label")}
            onChange={async (e) => {
              try {
                setActiveSW(e.target.checked);

                if (e.target.checked) {
                  await addSelfWeights();
                } else {
                  await delSelfWeights();
                }
              } catch (x) {
                report_exception(t, x);
              }
            }}
            checked={activeSW}
          />
        </Row>
      </Container>

      {/* VERTICAL POSITION */}
      <Row className="p-0 m-0">
        <Col xs={11} style={{ color: "#696969", marginBottom: 5 }}>
          ↓ <span className="header">{t("pointload_vertical_position_.label")}</span>
        </Col>
      </Row>

      <Container className="sx-table-container">
        <Table hover size="sm" className="main-sx-table">
          <thead>
            {/* TABLE HEADER */}
            <tr>
              <th>X({lengthUnit})</th>
              <th>Q({forceUnit})</th>

              <th>
                e<sub>y</sub> ({sectionalUnit})
              </th>

              <th>
                e<sub>z</sub> ({sectionalUnit})
              </th>

              <th style={{ width: 80 }}>{t("loadcase_.label")}</th>

              <th style={{ width: 60 }}>&nbsp;</th>

              <th style={{ border: 0, width: 40 }} />
            </tr>
          </thead>

          {/* TABLE BODY */}
          <tbody>
            {loadsVertical.length > 0 &&
              loadsVertical.flatMap((load, index) => (
                <tr
                  key={load.id?.guid || index}
                  id={`loadsVerticalRow_${index}`}
                  className="table-body align-items-center"
                >
                  <td className="text-center">
                    <SXEditField
                      type="number"
                      id={"vert_posx_" + index}
                      textAlign={"left"}
                      tableView
                      disableNegativeNumber
                      onUpdate={(value: number) => {
                        if (load.id?.guid) {
                          updateLoadPosition(load.id.guid, 0, { x: clampPosition(fromDisplayLength(value)) });
                        }
                      }}
                      displayUnit={false}
                      val={toDisplayLength(load.positions?.[0]?.x || 0)}
                    />
                  </td>

                  <td className="">
                    <SXEditField
                      type="number"
                      id={"vert_valz_" + index}
                      textAlign={"left"}
                      tableView
                      disableNegativeNumber
                      onUpdate={(value: number) => {
                        if (load.id?.guid) {
                          updateLoadValue(load.id.guid, 0, fromDisplayForce(value));
                        }
                      }}
                      displayUnit={false}
                      val={toDisplayForce(load.values?.[0] || 0)}
                    />
                  </td>

                  <td className="" style={{ height: 30 }}>
                    <SXEditField
                      type="number"
                      id={"eccy_" + index}
                      textAlign="left"
                      tableView
                      disableNegativeNumber={false}
                      onUpdate={(value: number) => {
                        if (load.id?.guid) {
                          updateLoadPosition(load.id.guid, 0, { y: fromDisplaySectionalLength(value) });
                        }
                      }}
                      displayUnit={false}
                      val={toDisplaySectionalLength(load.positions?.[0]?.y || 0)}
                    />
                  </td>

                  <td className="" style={{ height: 30 }}>
                    <SXEditField
                      type="number"
                      id={"vert_eccz_" + index}
                      textAlign="left"
                      tableView
                      disableNegativeNumber={false}
                      onUpdate={(value: number) => {
                        if (load.id?.guid) {
                          updateLoadPosition(load.id.guid, 0, { z: fromDisplaySectionalLength(value) });
                        }
                      }}
                      displayUnit={false}
                      val={toDisplaySectionalLength(load.positions?.[0]?.z || 0)}
                    />
                  </td>

                  <td className="text-center" style={{ height: 30 }}>
                    <div className="d-flex align-items-center" style={{ gap: 8 }}>
                      {selProject && <LoadCaseColor project={selProject} guid={load.lcaseGuid || ""} />}
                      <SXDropdown
                        name={"vertical_loadcase_" + index}
                        id={"vertical_loadcase_" + index}
                        lang=""
                        localisation={false}
                        tableView
                        list={loadCaseOptions}
                        val={loadCaseOptions.find((lc) => lc.id === (load.lcaseGuid || "")) || null}
                        onUpdate={(value) => {
                          if (load.id?.guid && value?.id) {
                            updateLoad(load.id.guid, { lcaseGuid: value.id });
                          }
                        }}
                      />
                    </div>
                  </td>

                  {/* <td className="text-center" style={{ height: 30 }}>
                    <ViewIcon size={16} />
                  </td> */}

                  <td style={{ border: 0 }}>
                    <span
                      onClick={() => {
                        if (load.id?.guid) {
                          deleteLoad(load.id.guid);
                        }
                      }}
                      className="delete-row-btn"
                      id={"delVerticalLoad_" + index}
                    >
                      <TimesIcon size={12} />
                    </span>
                  </td>
                </tr>
              ))}
          </tbody>
        </Table>

        {/* BOTTOM ROW */}
        <Row>
          <div className="text-right" style={{ paddingRight: 10 }}>
            <span
              className="addrow add-row-btn pointer"
              onClick={() => {
                addNewVerticalLoad(elementLength);
              }}
              id={"addNewVerticalLoad"}
              data-testid={"addNewVerticalLoad"}
            >
              <PlusIcon size={16} />
            </span>
          </div>
        </Row>
      </Container>

      {/* HORIZONTAL POSITION */}
      <Row>
        <Col xs={11} style={{ color: "#696969", marginBottom: 5 }}>
          <span className="header"> → {t("pointload_horizontal_position_.label")}</span>
        </Col>
      </Row>

      <Container className="sx-table-container">
        <Table hover size="sm" className="main-sx-table">
          <thead>
            <tr>
              <th>X({lengthUnit})</th>
              <th>
                {t("pointload_value_force_.label")} ({forceUnit})
              </th>
              <th>{t("direction_.label")}</th>
              <th style={{ width: 80 }}>{t("loadcase_.label")}</th>
              <th style={{ width: 60 }}>&nbsp;</th>
              <th style={{ border: 0, width: 40 }} />
            </tr>
          </thead>
          <tbody>
            {loadsHorizontal.length > 0 &&
              loadsHorizontal.flatMap((load, index) => (
                <tr
                  key={`${"pointHorloadRow_" + index}`}
                  id={`${"pointHorloadRow_" + index}`}
                  className="table-body align-items-center"
                >
                  <td className="text-center">
                    <SXEditField
                      type="number"
                      id={"hposx_" + index}
                      tableView
                      textAlign="left"
                      disableNegativeNumber={true}
                      onUpdate={(value: number) => {
                        if (load.id?.guid) {
                          updateLoadPosition(load.id.guid, 0, { x: clampPosition(fromDisplayLength(value)) });
                        }
                      }}
                      displayUnit={false}
                      val={toDisplayLength(load.positions?.[0]?.x || 0)}
                    />
                  </td>

                  <td className="text-center">
                    {/* a negative value acts in the opposite direction of the chosen axis, so negative input is allowed here */}
                    <SXEditField
                      type="number"
                      id={"hval_" + index}
                      tableView
                      textAlign="left"
                      disableNegativeNumber={false}
                      onUpdate={(value: number) => {
                        if (load.id?.guid) {
                          updateLoadValue(load.id.guid, 0, fromDisplayForce(value));
                        }
                      }}
                      displayUnit={false}
                      val={toDisplayForce(load.values?.[0] || 0)}
                    />
                  </td>

                  <td>
                    <SXDropdown
                      name={"horizontal_direction_" + index}
                      id={"horizontal_direction_" + index}
                      lang=""
                      localisation={false}
                      tableView
                      list={horizontalDirectionOptions}
                      val={
                        horizontalDirectionOptions.find((d) => d.id === (load.direction?.z === 1 ? "Z" : "Y")) ||
                        horizontalDirectionOptions[0]
                      }
                      onUpdate={(value) => {
                        if (load.id?.guid && value?.id) {
                          const direction =
                            value.id === "Z"
                              ? ng.geometry.Vector3D.create({ x: 0, y: 0, z: 1 })
                              : ng.geometry.Vector3D.create({ x: 0, y: 1, z: 0 });
                          updateLoad(load.id.guid, { direction });
                        }
                      }}
                    />
                  </td>

                  <td className="text-center" style={{ height: 30 }}>
                    <div className="d-flex align-items-center" style={{ gap: 8 }}>
                      {selProject && <LoadCaseColor project={selProject} guid={load.lcaseGuid || ""} />}
                      <SXDropdown
                        name={"horizontal_loadcase_" + index}
                        id={"horizontal_loadcase_" + index}
                        lang=""
                        localisation={false}
                        tableView
                        list={loadCaseOptions}
                        val={loadCaseOptions.find((lc) => lc.id === (load.lcaseGuid || "")) || null}
                        onUpdate={(value) => {
                          if (load.id?.guid && value?.id) {
                            updateLoad(load.id.guid, { lcaseGuid: value.id });
                          }
                        }}
                      />
                    </div>
                  </td>
                  {/* <td className="text-center" style={{ height: 30 }}>
                    <ViewIcon size={16} />
                  </td> */}

                  <td style={{ border: 0 }}>
                    <span
                      onClick={() => {
                        if (load.id?.guid) {
                          deleteLoad(load.id.guid);
                        }
                      }}
                      className="delete-row-btn"
                      id={"delHorizontalLoad_" + index}
                    >
                      <TimesIcon size={12} />
                    </span>
                  </td>
                </tr>
              ))}
          </tbody>
        </Table>

        {/* BOTTOM ROW */}
        <Row>
          <div className="text-right" style={{ paddingRight: 10 }}>
            <span
              className="addrow add-row-btn pointer"
              onClick={() => {
                addNewHorizontalLoad(elementLength);
              }}
              id={"addNewHorizontalLoad"}
              data-testid={"addNewHorizontalLoad"}
            >
              <PlusIcon size={16} />
            </span>
          </div>
        </Row>
      </Container>

      {/* LINE LOAD */}
      <Row>
        <Col xs={11} style={{ color: "#696969", marginBottom: 5 }}>
          <span className="header"> ⇉ {t("lineload_.label")}</span>
        </Col>
      </Row>
      <Container className="sx-table-container">
        <Table hover size="sm" className="main-sx-table">
          <thead>
            <tr>
              <th>
                X<sub>0</sub>({lengthUnit})
              </th>
              <th>
                X<sub>1</sub>({lengthUnit})
              </th>
              <th>
                q<sub>0</sub> ({lineLoadUnit})
              </th>
              <th>
                q<sub>1</sub> ({lineLoadUnit})
              </th>
              <th>{t("direction_.label")}</th>
              <th style={{ width: 80 }}>{t("loadcase_.label")}</th>
              <th style={{ width: 60 }}>&nbsp;</th>
              <th style={{ border: 0, width: 40 }} />
            </tr>
          </thead>
          <tbody>
            {lineLoads.length > 0 &&
              lineLoads.flatMap((load, index) => {
                //if self weight load, disable editing and show in vertical direction only
                let lcaGuid = lineLoads?.[index]?.lcaseGuid || "";
                let lca = loadCases?.find((lc) => lc.id?.guid === lcaGuid);
                let sw = lca?.type === ng.loadcase.Type.TYPE_SELF_WEIGHT ? true : false;
                return (
                  <tr
                    key={load.id?.guid || `${"lineLoadRow_" + index}`}
                    id={`${"lineLoadRow_" + index}`}
                    className="table-body align-items-center"
                  >
                    <td className="text-center">
                      <SXEditField
                        disabled={sw}
                        type="number"
                        id={"line_x0_" + index}
                        tableView
                        textAlign="left"
                        disableNegativeNumber={true}
                        onUpdate={(value: number) => {
                          if (load.id?.guid) {
                            updateLoadPosition(load.id.guid, 0, { x: clampPosition(fromDisplayLength(value)) });
                          }
                        }}
                        displayUnit={false}
                        val={toDisplayLength(load.positions?.[0]?.x || 0)}
                      />
                    </td>
                    <td>
                      <SXEditField
                        disabled={sw}
                        type="number"
                        id={"line_x1_" + index}
                        tableView
                        textAlign="left"
                        disableNegativeNumber={true}
                        onUpdate={(value: number) => {
                          if (load.id?.guid) {
                            updateLoadPosition(load.id.guid, 1, { x: clampPosition(fromDisplayLength(value)) });
                          }
                        }}
                        displayUnit={false}
                        val={toDisplayLength(load.positions?.[1]?.x || 0)}
                      />
                    </td>
                    <td style={{ height: 30 }}>
                      {/* a negative value acts in the opposite direction of the chosen axis, so negative input is allowed here */}
                      <SXEditField
                        disabled={sw}
                        type="number"
                        id={"line_q0_" + index}
                        tableView
                        textAlign="left"
                        disableNegativeNumber={false}
                        onUpdate={(value: number) => {
                          if (load.id?.guid) {
                            // q0 and q1 are equal for almost all line loads, so entering a new
                            // q0 fills q1 with the same value. A varying load is made by editing
                            // q1 afterwards. Only mirror on an actual change, otherwise just
                            // leaving the field (blur) would wipe out a deviating q1.
                            // Compare on the value as it is shown (3 decimals), so that
                            // re-entering the same number is not treated as a change.
                            const q0Changed =
                              Math.round(value * 1000) !== Math.round(toDisplayLineLoad(load.values?.[0] || 0) * 1000);
                            updateLoadValue(load.id.guid, 0, fromDisplayLineLoad(value));
                            if (q0Changed) {
                              updateLoadValue(load.id.guid, 1, fromDisplayLineLoad(value));
                            }
                          }
                        }}
                        displayUnit={false}
                        val={toDisplayLineLoad(load.values?.[0] || 0)}
                      />
                    </td>
                    <td style={{ height: 30 }}>
                      {/* a negative value acts in the opposite direction of the chosen axis, so negative input is allowed here */}
                      <SXEditField
                        disabled={sw}
                        type="number"
                        id={"line_q1_" + index}
                        tableView
                        textAlign="left"
                        disableNegativeNumber={false}
                        onUpdate={(value: number) => {
                          if (load.id?.guid) {
                            updateLoadValue(load.id.guid, 1, fromDisplayLineLoad(value));
                          }
                        }}
                        displayUnit={false}
                        val={toDisplayLineLoad(load.values?.[1] || 0)}
                      />
                    </td>
                    <td style={{ height: 30 }}>
                      <SXDropdown
                        disabled={sw}
                        tableView
                        name={"line_direction_" + index}
                        id={"line_direction_" + index}
                        lang=""
                        localisation={false}
                        list={sw ? verticalDirectionOptions : horizontalDirectionOptions}
                        val={
                          sw
                            ? verticalDirectionOptions[0]
                            : horizontalDirectionOptions.find((d) => d.id === (load.direction?.z === 1 ? "Z" : "Y")) ||
                              horizontalDirectionOptions[0]
                        }
                        onUpdate={(value) => {
                          if (load.id?.guid && value?.id) {
                            const direction =
                              value.id === "Z"
                                ? ng.geometry.Vector3D.create({ x: 0, y: 0, z: 1 })
                                : ng.geometry.Vector3D.create({ x: 0, y: 1, z: 0 });
                            updateLoad(load.id.guid, { direction });
                          }
                        }}
                      />
                    </td>
                    <td className="text-center" style={{ height: 30 }}>
                      <div className="d-flex align-items-center" style={{ gap: 8 }}>
                        {selProject && <LoadCaseColor project={selProject} guid={load.lcaseGuid || ""} />}
                        <SXDropdown
                          disabled={sw}
                          name={"line_loadcase_" + index}
                          id={"line_loadcase_" + index}
                          lang=""
                          localisation={false}
                          tableView
                          list={loadCaseOptions}
                          val={loadCaseOptions.find((lc) => lc.id === (load.lcaseGuid || "")) || null}
                          onUpdate={(value) => {
                            if (load.id?.guid && value?.id) {
                              updateLoad(load.id.guid, { lcaseGuid: value.id });
                            }
                          }}
                        />
                      </div>
                    </td>
                    {/* <td className="text-center" style={{ height: 30 }}>
                      <ViewIcon size={16} />
                    </td> */}

                    {!sw && (
                      <td style={{ border: 0 }}>
                        <span
                          onClick={() => {
                            if (load.id?.guid) {
                              deleteLoad(load.id.guid);
                            }
                          }}
                          className="delete-row-btn"
                          id={"delLineLoad_" + index}
                        >
                          <TimesIcon size={12} />
                        </span>
                      </td>
                    )}
                  </tr>
                );
              })}
          </tbody>
        </Table>
        {/* BOTTOM ROW */}
        <Row>
          <div className="text-right" style={{ paddingRight: 10 }}>
            <span
              className="addrow add-row-btn pointer"
              onClick={() => {
                addNewLineLoad(elementLength);
              }}
              id={"addNewLineLoad"}
              data-testid={"addNewLineLoad"}
            >
              <PlusIcon size={16} />
            </span>
          </div>
        </Row>
      </Container>
    </Container>
  );
};
